import { firestore } from "firebase";
import { useState } from "react";
import { createContainer } from "unstated-next";
import { CampaignService } from "../services/CampaignService";
import { Timestamp } from "../utilities/Firebase";

type CampaignList = Pick<Campaign, "id" | "title" | "image" | "from" | "to" | "targetBuildings" | "applicationURL">[]

type Campaign = {
    id: string,
    title: string,
    image: string,
    targetBuildings?: string[],
    applicationURL?: string,
    attention: LineElement[],
    privacyPolicy: LineElement[],
    description: LineElement[],
    finishedDescription: LineElement[],
    from: Date,
    to: Date,
    detail: string,
    labels: {
        reward1: string,
        reward2: string,
        progress1: string,
        progress2: string,
        completeMessage: LineElement[]
    },
    categories: category[],
    status: string,
    subContents: {
        questionnaire: SubContentBase & Questionnaire
    },
    pointNumberGuide: string,
    newMemberCheck: string,
    contactForm: string,
}

type LineElement = {
    text: string,
    style?: string[],
    isInline?: boolean
}

type category = {
    category: string,
    icon: string,
    label: string
}

type SubContentBase = {
    name: string,
    contents: unknown,
    setting?: unknown
}

type Questionnaire = {
    contents: {
        title: string,
        description: string[],
        introTitle: string,
        introMessage: string[],
        completeMessage: string[],
        questions: {
            qId: string,
            question: string,
            choices: string[],
            multiple: boolean,
            require: boolean
        }[]
    }
}

/**
 * キャンペーンコンテナ
 */
const useCampaignContainer = () => {
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [campaignList, setCampaignList] = useState<CampaignList | null>(null)

    /**
     * キャンペーンデータ初期化
     * @param id キャンペーンID
     */
    const initCampaign = async (id: string) => {
        const campaignData = await CampaignService().getCampaign(id);

        if (campaignData) {
            const campaign = {
                id: id,
                title: campaignData.title,
                image: campaignData.mainImage || campaignData.image,
                attention: campaignData.attention,
                description: campaignData.description,
                finishedDescription: campaignData.finishedDescription,
                from: campaignData.from.toDate(),
                to: campaignData.to.toDate(),
                detail: campaignData.detail,
                labels: campaignData.labels,
                categories: campaignData.setting.categories,
                status: getCampaignStatus(campaignData),
                subContents: campaignData.subContents,
                pointNumberGuide: campaignData.pointNumberGuide,
                newMemberCheck: campaignData.newMemberCheck,
                contactForm: campaignData.contactForm,
                privacyPolicy: campaignData.privacyPolicy
            };
            setCampaign({ ...campaign });
        }
    };

    const getCampaignStatus = (campaignData: any) => {
        const current = Timestamp.now();

        if (campaignData.from > current) {
            return "preparing";
        }

        if (campaignData.to < current) {
            return "end";
        }

        return "active";
    };

    /**
     * キャンペーンデータをリセットする
     */
    const resetCampaign = () => {
        setCampaign(null);
    };

    /**
     * 開催中のキャンペーンリストを取得する
     */
    const initCampaignList = async () => {
        const [campaignListData, setting] = await Promise.all([CampaignService().getCampaignList(), CampaignService().getSetting()]);
        if (campaignListData && setting) {
            const campaignList = campaignListData.map((campaignData: { id: string, data: firestore.DocumentData }) => {
                let targetBuildings: string[] = [];
                if (campaignData.data.building) {
                    // MMS管理対象の場合はsettingから対象館名を突合
                    targetBuildings.push(setting.buildings[campaignData.data.building].name);
                };

                if (campaignData.data.targetBuildings) {
                    // MMS管理対象外の場合は設定してある対象館名を設定
                    targetBuildings = campaignData.data.targetBuildings;
                };

                return {
                    id: campaignData.id,
                    title: campaignData.data.title,
                    image: campaignData.data.mainImage || campaignData.data.image,
                    from: campaignData.data.from.toDate(),
                    to: campaignData.data.to.toDate(),
                    targetBuildings: targetBuildings,
                    applicationURL: campaignData.data.applicationURL
                };
            });
            setCampaignList(campaignList);
        };
    };

    return { campaign, campaignList, initCampaign, resetCampaign, initCampaignList };
}

export const CampaignContainer = createContainer(useCampaignContainer);

